import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import type { IStepProps } from '@ingenico/react-ui/content-stepper';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useMemo, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SystemPackageVersionBuildStep } from '../components/system-package-version-build-step/SystemPackageVersionBuildStep.ctrl';
import { SystemPackageVersionConfigureStep } from '../components/system-package-version-configure-step/SystemPackageVersionConfigureStep.ctrl';

import type {
  UseAppVersionStepsReturn,
  UseSystemPackageVersionAppStepsParams,
} from '../components/system-package-version-steps/systemPackageVersionSteps.model';
import { AppVersionStepsKeys } from '../components/system-package-version-steps/systemPackageVersionSteps.model';
import {
  getDefinitionHiddenFields,
  verifyHasSystemPackageVersionBuildErrorsApartDeploymentRules,
  verifyHasSystemPackageVersionBuildErrorsDeploymentRulesOnly,
} from '../components/system-package-version-steps/utils/systemPackageVersionSteps.util';
import { SystemPackageVersionSummaryStep } from '../components/system-package-version-summary-step/SystemPackageVersionSummaryStep.ctrl';
import type { SystemPackageAppVersion } from '../pages/create-system-package-version-page/createSystemPackageVersionPage.model';

import { SystemPackageVersionDefinitionStep } from '../components/system-package-version-definition-step/SystemPackageVersionDefinitionStep.ctrl';
import { SystemPackageVersionDeploymentRulesStep } from '../components/system-package-version-deployment-rules-step/SystemPackageVersionDeploymentRulesStep.ctrl';
import styles from '../components/system-package-version-steps/systemPackageVersionSteps.module.scss';

export function useSystemPackageVersionAppSteps({
  moveToNextStep,
  moveToPreviousStep,
  control,
  validateNextIsDisabled,
  applicationFiles,
  existingTemplates,
  setFieldError,
  clearFieldError,
  definitionOptions,
  onClickCancel,
  hasPartialFileUpdateError,
  isLoadingTemplateResources,
  shouldEnableDownloadRules,
  triggerDeploymentRulesValidation,
}: UseSystemPackageVersionAppStepsParams): UseAppVersionStepsReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.APPLICATION, ALL_LNGS_NAMESPACES.COMMON]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const moveOn = useCallback((key: AppVersionStepsKeys) => () => moveToNextStep?.(key), [moveToNextStep]);

  const goBack = useCallback((key: AppVersionStepsKeys) => () => moveToPreviousStep?.(key), [moveToPreviousStep]);

  const fieldValues = useWatch({ control });
  const formState = useFormState<SystemPackageAppVersion>({ control });

  const handleUploadingProgress = useCallback((isUploading?: boolean) => {
    setIsUploadingFile(!!isUploading);
  }, []);

  const hasBuildFileErrors = useMemo(
    () =>
      (fieldValues as SystemPackageAppVersion).build?.applicationFiles?.some((appFile) =>
        appFile.template?.some((templateFile) => templateFile.error === true),
      ) ?? false,
    [fieldValues],
  );

  const hasConfigureFileErrors = useMemo(
    () =>
      (fieldValues as SystemPackageAppVersion).configure?.parameterTemplates?.some((pt) =>
        pt.template?.some((templateFile) => templateFile.error === true),
      ) ?? false,
    [fieldValues],
  );

  const steps = useMemo<IStepProps[]>(
    () => [
      {
        content: (
          <SystemPackageVersionDefinitionStep
            appDependencyOptions={definitionOptions?.appDependencyOptions}
            control={control}
            fieldsLoading={definitionOptions?.fieldsLoading}
            hiddenFields={getDefinitionHiddenFields()}
            minimumAppVersionOptions={definitionOptions?.minimumAppVersionOptions}
            minimumOSVersionOptions={definitionOptions?.minimumOSVersionOptions}
            minimumSystemPackageVersionOptions={definitionOptions?.minimumSystemPackageVersionOptions}
            operationalSystemDependencyOptions={definitionOptions?.operationalSystemDependencyOptions}
            systemPackageDependencyOptions={definitionOptions?.systemPackageDependencyOptions}
          />
        ),
        stepTitle: t('title.definitionStep'),
        titleCancelButton: t('common:button.cancel'),
        titleNextButton: t('common:button.next'),
        nextButtonIcon: <ArrowForwardIcon />,
        onHandleNext: moveOn(AppVersionStepsKeys.Define),
        isButtonDisabled: validateNextIsDisabled?.(AppVersionStepsKeys.Define),
        isStartPreviousIcon: true,
        onHandlePrevious: goBack(AppVersionStepsKeys.Define),
        onHandleCancel: onClickCancel,
      },
      {
        content: (
          <SystemPackageVersionBuildStep
            applicationFiles={applicationFiles}
            clearFieldError={clearFieldError}
            control={control}
            setFieldError={setFieldError}
            getUploadingProgress={handleUploadingProgress}
          />
        ),
        stepTitle: t('title.buildStep'),
        titleCancelButton: t('common:button.cancel'),
        titleNextButton: t('common:button.next'),
        nextButtonIcon: <ArrowForwardIcon />,
        onHandleNext: moveOn(AppVersionStepsKeys.Build),
        isButtonDisabled: validateNextIsDisabled?.(AppVersionStepsKeys.Build) || isUploadingFile || hasBuildFileErrors,
        titlePreviousButton: t('common:button.back'),
        previousButtonIcon: <ArrowForwardIcon className={styles.previousButtonIcon} />,
        isStartPreviousIcon: true,
        onHandlePrevious: goBack(AppVersionStepsKeys.Build),
        onHandleCancel: onClickCancel,
        isPrevButtonDisabled: isUploadingFile,
        error: verifyHasSystemPackageVersionBuildErrorsApartDeploymentRules(formState.errors?.build),
      },
      ...(shouldEnableDownloadRules
        ? [
            {
              content: (
                <SystemPackageVersionDeploymentRulesStep
                  control={control}
                  applicationFiles={applicationFiles}
                  triggerValidation={triggerDeploymentRulesValidation}
                />
              ),
              stepTitle: t('title.downloadRulesStep', { ns: ALL_LNGS_NAMESPACES.APPLICATION }),
              titleCancelButton: t('common:button.cancel'),
              titleNextButton: t('common:button.next'),
              nextButtonIcon: <ArrowForwardIcon />,
              onHandleNext: moveOn(AppVersionStepsKeys.DeploymentRules),
              isButtonDisabled: validateNextIsDisabled?.(AppVersionStepsKeys.DeploymentRules),
              titlePreviousButton: t('common:button.back'),
              previousButtonIcon: <ArrowForwardIcon className={styles.previousButtonIcon} />,
              isStartPreviousIcon: true,
              onHandlePrevious: goBack(AppVersionStepsKeys.DeploymentRules),
              onHandleCancel: onClickCancel,
              isPrevButtonDisabled: isUploadingFile,
              error: verifyHasSystemPackageVersionBuildErrorsDeploymentRulesOnly(formState.errors?.build),
            },
          ]
        : []),
      {
        content: (
          <SystemPackageVersionConfigureStep
            clearFieldError={clearFieldError}
            control={control}
            existingTemplates={existingTemplates}
            setFieldError={setFieldError}
            getUploadingProgress={handleUploadingProgress}
          />
        ),
        stepTitle: t('title.configureStep'),
        titleCancelButton: t('common:button.cancel'),
        titleNextButton: t('common:button.next'),
        nextButtonIcon: <ArrowForwardIcon />,
        onHandleNext: moveOn(AppVersionStepsKeys.Configure),
        isButtonDisabled:
          validateNextIsDisabled?.(AppVersionStepsKeys.Configure) || isUploadingFile || hasConfigureFileErrors,
        titlePreviousButton: t('common:button.back'),
        previousButtonIcon: <ArrowForwardIcon className={styles.previousButtonIcon} />,
        isStartPreviousIcon: true,
        onHandlePrevious: goBack(AppVersionStepsKeys.Configure),
        onHandleCancel: onClickCancel,
        isPrevButtonDisabled: isUploadingFile,
      },
      {
        content: (
          <SystemPackageVersionSummaryStep
            appDependencyOptions={definitionOptions?.appDependencyOptions}
            applicationFiles={applicationFiles}
            data={fieldValues as SystemPackageAppVersion}
            existingTemplates={existingTemplates}
            isLoadingTemplateResources={isLoadingTemplateResources}
            operationalSystemDependencyOptions={definitionOptions?.operationalSystemDependencyOptions}
            systemPackageDependencyOptions={definitionOptions?.systemPackageDependencyOptions}
          />
        ),
        stepTitle: t('title.summaryStep'),
        titleCancelButton: t('common:button.cancel'),
        titleNextButton: hasPartialFileUpdateError ? t('common:button.retry') : t('button.addVersion'),
        nextButtonIcon: hasPartialFileUpdateError ? <RefreshIcon /> : <CheckIcon />,
        onHandleNext: moveOn(AppVersionStepsKeys.Summary),
        isButtonDisabled: validateNextIsDisabled?.(AppVersionStepsKeys.Summary),
        titlePreviousButton: t('common:button.back'),
        previousButtonIcon: <ArrowForwardIcon className={styles.previousButtonIcon} />,
        isStartPreviousIcon: true,
        isStartNextIcon: true,
        onHandlePrevious: goBack(AppVersionStepsKeys.Summary),
        onHandleCancel: onClickCancel,
      },
    ],
    [
      definitionOptions?.appDependencyOptions,
      definitionOptions?.fieldsLoading,
      definitionOptions?.minimumAppVersionOptions,
      definitionOptions?.minimumOSVersionOptions,
      definitionOptions?.minimumSystemPackageVersionOptions,
      definitionOptions?.operationalSystemDependencyOptions,
      definitionOptions?.systemPackageDependencyOptions,
      control,
      t,
      moveOn,
      validateNextIsDisabled,
      goBack,
      onClickCancel,
      applicationFiles,
      clearFieldError,
      setFieldError,
      handleUploadingProgress,
      isUploadingFile,
      shouldEnableDownloadRules,
      triggerDeploymentRulesValidation,
      existingTemplates,
      fieldValues,
      isLoadingTemplateResources,
      hasPartialFileUpdateError,
      formState.errors?.build,
      hasBuildFileErrors,
      hasConfigureFileErrors,
    ],
  );

  return useMemo(() => ({ steps }), [steps]);
}
